import {Component, OnInit, Input, Output, EventEmitter} from '@angular/core';
import {FormBuilder, FormGroup, Validators, FormControl} from '@angular/forms';
import {cryptoGrammeValidator, numeroValidator, monthValidator, detectCardType} from './validator';
import * as moment from 'moment';
import {SiteInfosService, WindowRefService} from '@app/core';
import {TranslateService} from '@ngx-translate/core';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {FineprintComponent} from "@app/shared/components/modals/fineprint/fineprint.component";
import {MatDialog} from '@angular/material';

@Component({
    selector: 'app-card',
    templateUrl: './card.component.html',
    styleUrls: ['./card.component.scss']
})
export class CardComponent implements OnInit {
    @Input() infos: {textBtnSave: string, submitted: boolean};
    @Output() savedCard = new EventEmitter<{status: boolean, cardForm: FormGroup}>();

    listCart = [];
    displayedCardLogos: string[] = [];
    cardholderName = '';
    nameTouched = false;
    displayCardNumber = '';
    displayExpiry = '';
    detectedBrand = '';
    unsupportedCardType = false;
    expiryTouched = false;
    listMonth = Array.from((new Array(12).keys())).map(i => 1 + i);
    listYear = Array.from((new Array(15).keys())).map(i => parseInt(moment().locale('en').format('YYYY')) + i);

    cartForm: FormGroup;

    hasFineprint: boolean = false;
    fineprintid: number = 0;
    fineprintText: any = '';
    data: any = {};

    constructor(
        private fb: FormBuilder,
        private siteInfosService: SiteInfosService,
        private modalService: NgbModal,
        public dialog: MatDialog,
        public translate: TranslateService
    ) {}

    ngOnInit() {
        this.cartForm = this.fb.group({
            firstname: ['', Validators.required],
            lastname: ['', Validators.required],
            'type': ['', Validators.required],
            numero: ['', Validators.compose([numeroValidator, Validators.required])],
            cryptogramme: ['', Validators.compose([Validators.required, cryptoGrammeValidator])],
            month: ['', Validators.compose([Validators.required, Validators.min(1), Validators.max(12), monthValidator])],
            year: ['', Validators.compose([Validators.required, Validators.min(parseInt(moment().format('YYYY'))), Validators.max(parseInt(moment().format('YYYY')) + 19)])],
        });
        this.listCart = this.siteInfosService.getListCard() || [];
        this.displayedCardLogos = this.listCart.filter(type => !!this.cardLogo(type));
        this.hasFineprint = this.siteInfosService.getIdFineprint() > 0;
        this.fineprintText = this.translate.get('toknow.'+this.siteInfosService.getIdFineprint()+'-description');
        const etabData = this.siteInfosService.getEtabData();
        this.data = {
            etabName: etabData['etablissementNom'],
            adresse: etabData['etabAdresse'],
            codePostal: etabData['etabCodepost'],
            city: etabData['etabVille'],
            country: etabData['etabPays'],
            email: etabData['etabMail'][0]
        };
        if (this.hasFineprint) {
            this.cartForm.addControl('fineprint', new FormControl(false, Validators.required));
            this.fineprintid = this.siteInfosService.getIdFineprint();
        }
    }

    onTypeCardChange() {
        this.cartForm.get('numero').updateValueAndValidity();
        this.cartForm.get('cryptogramme').updateValueAndValidity();
    }

    onCardholderNameInput(value: string) {
        this.cardholderName = value || '';
        const parts = this.cardholderName.trim().split(/\s+/).filter(Boolean);
        this.cartForm.get('firstname').setValue(parts.length ? parts[0] : '');
        this.cartForm.get('lastname').setValue(parts.length > 1 ? parts.slice(1).join(' ') : '');
    }

    get nameInvalid(): boolean {
        return this.nameTouched && (this.cartForm.get('firstname').invalid || this.cartForm.get('lastname').invalid);
    }

    get cardNumberValid(): boolean {
        return !!this.detectedBrand && this.cartForm.get('numero').valid && this.cartForm.get('type').valid;
    }

    get cvcValid(): boolean {
        return this.cartForm.get('cryptogramme').valid;
    }

    get expiryValid(): boolean {
        return this.cartForm.get('month').valid && this.cartForm.get('year').valid;
    }

    cardLogo(type: string): string {
        if (type === 'visa') return 'card-logo-visa';
        if (type === 'euromastercard' || type === 'mastercard') return 'card-logo-mastercard';
        if (type === 'americanexpress' || type === 'amex') return 'card-logo-american-express';
        if (type === 'jcb') return 'card-logo-jcb';
        if (type === 'dinersclub') return 'card-logo-diners';
        if (type === 'discover') return 'card-logo-discover';
        if (type === 'unionpay') return 'card-logo-unionpay';
        if (type === 'bc') return 'card-logo-cartebancaire';
        return '';
    }

    onCardNumberInput(input: HTMLInputElement) {
        const digits = (input.value || '').replace(/\D/g, '').slice(0, 19);
        const matches = detectCardType(digits);
        const supported = (this.listCart || []).filter(type => matches.indexOf(type) !== -1);
        this.detectedBrand = supported.length ? supported[0] : '';
        this.unsupportedCardType = digits.length >= 6 && matches.indexOf('unknown') === -1 && !supported.length;
        const groupSizes = matches.indexOf('americanexpress') !== -1 ? [4, 6, 5] : [4, 4, 4, 4, 3];
        let offset = 0;
        this.displayCardNumber = groupSizes.map(size => {
            const group = digits.slice(offset, offset + size);
            offset += size;
            return group;
        }).filter(Boolean).join(' ');
        input.value = this.displayCardNumber;
        this.cartForm.get('type').setValue(this.detectedBrand);
        this.cartForm.get('numero').setValue(digits);
        if (this.unsupportedCardType || digits.length >= 15) this.cartForm.get('numero').markAsTouched();
        this.cartForm.get('cryptogramme').updateValueAndValidity();
    }

    onExpiryInput(input: HTMLInputElement) {
        const digits = (input.value || '').replace(/\D/g, '').slice(0, 4);
        this.displayExpiry = digits.length > 2 ? digits.slice(0, 2) + '/' + digits.slice(2) : digits;
        input.value = this.displayExpiry;
        this.cartForm.get('month').setValue(digits.length >= 2 ? parseInt(digits.slice(0, 2), 10) : '');
        this.cartForm.get('year').setValue(digits.length === 4 ? 2000 + parseInt(digits.slice(2), 10) : '');
        this.cartForm.get('month').updateValueAndValidity();
        if (digits.length === 4) this.expiryTouched = true;
    }

    onExpiryBlur() {
        this.expiryTouched = true;
        this.cartForm.get('month').markAsTouched();
        this.cartForm.get('year').markAsTouched();
    }

    get expiryInvalid(): boolean {
        return this.expiryTouched && (this.cartForm.get('month').invalid || this.cartForm.get('year').invalid);
    }

    onYearCardChange() {
        this.cartForm.get('month').updateValueAndValidity();
    }

    // openFineprint(content) {
    //     this.modalService.open(content, {size: 'lg', ariaLabelledBy: 'modal-basic-title'}).result.then((result) => {
    //         //            console.log(`Closed with: ${result}`);
    //     }, (reason) => {
    //         //            console.log(`Dismissed ${reason}`);
    //     });
    // }
    
    openDialog() {
        const dialogRef = this.dialog.open(FineprintComponent, {
            data: {fineprint: this.fineprintText }
        });
        dialogRef.afterClosed().subscribe(result => {
            
        });
    }

    openPrivacy(content) {
        this.modalService.open(content, {size: 'lg', ariaLabelledBy: 'modal-privacy'}).result.then((result) => {
            //            console.log(`Closed with: ${result}`);
        }, (reason) => {
            //            console.log(`Dismissed ${reason}`);
        });
    }

    save() {
        Object.keys(this.cartForm.controls).forEach(key => this.cartForm.get(key).markAsTouched());
        this.nameTouched = true;
        this.expiryTouched = true;
        if (this.hasFineprint) {
            this.cartForm.get('fineprint').markAsTouched();
            this.cartForm.get('fineprint').updateValueAndValidity();
        }
        this.cartForm.get('month').updateValueAndValidity();
        if (this.cartForm.valid) {
            this.savedCard.emit({status: true, cardForm: this.cartForm});
        } else {
            this.savedCard.emit({status: false, cardForm: this.cartForm});
        }
    }

}
